import React from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { RewardCatalogIcon } from '@/src/renderer/components/ui/RewardCatalogIcon'
import { getRewardById, rewardCta, type CatalogLanguage } from '@/src/renderer/lib/data/rewardCatalog'
import { TaskDropdown } from '../TaskDropdown'
import { RewardPickerContent } from '../RewardPicker'
import type { CollapsedDropdowns } from './hooks/useCollapsedDropdowns'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

interface CollapsedRewardIconProps {
  isExpanded: boolean
  /** Catalogue reward id attached to the task, if any. */
  rewardId: string | null | undefined
  dropdowns: CollapsedDropdowns
  /**
   * Commits a newly picked reward (form state + Dexie + usage history).
   * `null` arrives when the picker's trash button detaches the reward.
   */
  onRewardChange: (rewardId: string | null) => void
  selectOnDropdownOpen: (open: boolean) => void
}

/**
 * The attached reward, shown right behind the title in the collapsed row —
 * icon only, at 32px so the artwork stays readable at a glance. Clicking it
 * opens the picker to swap the reward without expanding the task, the same
 * way the start-date icon opens its date picker. The CTA ("Einen Kristall
 * sammeln") rides along as the accessible label; the expanded row spells it
 * out in {@link TaskRewardTrigger}.
 *
 * Collapses width-first on expand, like the attention / start-date icons in
 * {@link CollapsedTitleIcons} — same timing, so the whole title row resolves
 * as one movement. The component stays mounted and gates on `isExpanded`
 * internally, which is what keeps the exit animation alive.
 *
 * `data-row-fit="title-icon"` puts its width into the adaptive-row
 * measurement, so a reward never pushes the title into a wrong line break.
 */
export const CollapsedRewardIcon: React.FC<CollapsedRewardIconProps> = ({
  isExpanded, rewardId, dropdowns, onRewardChange, selectOnDropdownOpen,
}) => {
  const { i18n } = useTranslation()
  const language: CatalogLanguage = i18n.language?.startsWith('en') ? 'en' : 'de'

  const reward = getRewardById(rewardId)
  const label = reward ? rewardCta(reward, language) : ''

  return (
    <AnimatePresence initial={false}>
      {!isExpanded && reward && (
        <motion.span
          key="reward-icon"
          className="task-entry__reward-icon"
          data-row-fit="title-icon"
          initial={{ width: 0, opacity: 0, marginLeft: 0 }}
          animate={{ width: 32, opacity: 1, marginLeft: 8 }}
          // The icon fades out faster than it collapses. Its width has to
          // shrink over the full 220ms so the row's layout stays smooth, but
          // during that time the title changes width underneath it — whatever
          // sideways drift is left should be over before the eye can follow it.
          exit={{
            width: 0,
            opacity: 0,
            marginLeft: 0,
            scale: 0.7,
            transition: { duration: 0.22, ease: [0.4, 0, 0.2, 1], opacity: { duration: 0.08 }, scale: { duration: 0.08 } },
          }}
          transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
          // Clicking the icon must not toggle the row open/closed underneath
          // the picker — same guard the other collapsed badges use.
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onMouseEnter={() => dropdowns.switchTo('reward')}
        >
          <TaskDropdown
            open={dropdowns.rewardOpen}
            onOpenChange={(open) => { dropdowns.setRewardOpen(open); selectOnDropdownOpen(open) }}
            side="bottom"
            align="start"
            alignOffset={-16}
            trigger={
              <WithTooltip content={label}>
                <button
                  className="task-entry__reward-icon-btn"
                  type="button"
                  aria-label={label}
                >
                  <RewardCatalogIcon rewardId={reward.id} name="" size={32} />
                </button>
              </WithTooltip>
            }
          >
            <RewardPickerContent
              value={reward.id}
              onValueChange={onRewardChange}
              onClose={() => dropdowns.setRewardOpen(false)}
              placement="badge"
            />
          </TaskDropdown>
        </motion.span>
      )}
    </AnimatePresence>
  )
}
